/**
 * Comments API service - handles comment and notification API calls
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface CommentAuthor {
  id: string;
  displayName: string;
  avatarUrl?: string;
}

export interface Attachment {
  id: string;
  filename: string;
  contentType: string;
  attachmentType: string;
  sizeBytes: number;
  url?: string;
  thumbnailUrl?: string;
  status: string;
  uploadedById: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  taskId: string;
  authorId: string;
  author?: CommentAuthor;
  parentId?: string;
  content: string;
  commentType: 'comment' | 'system' | 'activity';
  mentions?: string[];
  reactions?: Record<string, string[]>;
  editedAt?: string;
  depth: number;
  replyCount: number;
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
}

export interface CommentWithReplies extends Comment {
  replies: Comment[];
}

export interface CreateCommentRequest {
  content: string;
  parentId?: string;
}

export interface UpdateCommentRequest {
  content: string;
}

export interface AttachmentUploadRequest {
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface AttachmentUploadResponse {
  attachmentId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface Notification {
  id: string;
  userId: string;
  notificationType: string;
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  actionUrl?: string;
  sourceTaskId?: string;
  sourceCommentId?: string;
  sourceProjectId?: string;
  sourceMeetingId?: string;
  actorId?: string;
  actor?: CommentAuthor;
  isRead: boolean;
  readAt?: string;
  isClicked: boolean;
  clickedAt?: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
}

// =============================================================================
// Comments API
// =============================================================================

export const commentsApi = {
  /**
   * Get all comments for a task
   */
  getTaskComments: async (
    taskId: string,
    options?: { includeReplies?: boolean; limit?: number; offset?: number }
  ): Promise<Comment[]> => {
    const params = new URLSearchParams();
    if (options?.includeReplies !== undefined) {
      params.set('include_replies', String(options.includeReplies));
    }
    if (options?.limit) {
      params.set('limit', String(options.limit));
    }
    if (options?.offset) {
      params.set('offset', String(options.offset));
    }
    const query = params.toString();
    const response = await apiClient.get<unknown[]>(
      `/tasks/${taskId}/comments${query ? `?${query}` : ''}`
    );
    return response.data.map(transformComment);
  },

  /**
   * Get a single comment with its replies
   */
  getComment: async (commentId: string): Promise<CommentWithReplies> => {
    const response = await apiClient.get<unknown>(`/comments/${commentId}`);
    const data = response.data as Record<string, unknown>;
    const comment = transformComment(data);
    return {
      ...comment,
      replies: ((data.replies as unknown[]) ?? []).map(transformComment),
    };
  },

  /**
   * Create a new comment on a task
   */
  createComment: async (taskId: string, data: CreateCommentRequest): Promise<Comment> => {
    const response = await apiClient.post<unknown>(`/tasks/${taskId}/comments`, {
      content: data.content,
      parent_id: data.parentId,
    });
    return transformComment(response.data);
  },

  /**
   * Update a comment
   */
  updateComment: async (commentId: string, data: UpdateCommentRequest): Promise<Comment> => {
    const response = await apiClient.patch<unknown>(`/comments/${commentId}`, {
      content: data.content,
    });
    return transformComment(response.data);
  },

  /**
   * Delete a comment
   */
  deleteComment: async (commentId: string): Promise<void> => {
    await apiClient.delete(`/comments/${commentId}`);
  },

  /**
   * Add a reaction to a comment
   */
  addReaction: async (commentId: string, emoji: string): Promise<Comment> => {
    const response = await apiClient.post<unknown>(`/comments/${commentId}/reactions`, { emoji });
    return transformComment(response.data);
  },

  /**
   * Remove a reaction from a comment
   */
  removeReaction: async (commentId: string, emoji: string): Promise<Comment> => {
    const response = await apiClient.delete<unknown>(
      `/comments/${commentId}/reactions/${encodeURIComponent(emoji)}`
    );
    return transformComment(response.data);
  },

  /**
   * Initiate a file upload for a comment attachment
   */
  initiateCommentAttachmentUpload: async (
    commentId: string,
    data: AttachmentUploadRequest
  ): Promise<AttachmentUploadResponse> => {
    const response = await apiClient.post<unknown>(
      `/comments/${commentId}/attachments`,
      {
        filename: data.filename,
        content_type: data.contentType,
        size_bytes: data.sizeBytes,
      }
    );
    return transformAttachmentUploadResponse(response.data);
  },

  /**
   * Initiate a file upload for a task attachment
   */
  initiateTaskAttachmentUpload: async (
    taskId: string,
    data: AttachmentUploadRequest
  ): Promise<AttachmentUploadResponse> => {
    const response = await apiClient.post<unknown>(
      `/tasks/${taskId}/attachments`,
      {
        filename: data.filename,
        content_type: data.contentType,
        size_bytes: data.sizeBytes,
      }
    );
    return transformAttachmentUploadResponse(response.data);
  },

  /**
   * Confirm that an attachment upload is complete
   */
  confirmAttachmentUpload: async (
    attachmentId: string,
    fileHash?: string
  ): Promise<Attachment> => {
    const response = await apiClient.post<unknown>(`/attachments/${attachmentId}/confirm`, {
      file_hash: fileHash,
    });
    return transformAttachment(response.data);
  },

  /**
   * Get all attachments for a task
   */
  getTaskAttachments: async (taskId: string): Promise<Attachment[]> => {
    const response = await apiClient.get<unknown[]>(`/tasks/${taskId}/attachments`);
    return response.data.map(transformAttachment);
  },

  /**
   * Get a specific attachment (refreshes download URL)
   */
  getAttachment: async (attachmentId: string): Promise<Attachment> => {
    const response = await apiClient.get<unknown>(`/attachments/${attachmentId}`);
    return transformAttachment(response.data);
  },

  /**
   * Delete an attachment
   */
  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },
};

// =============================================================================
// Notifications API
// =============================================================================

export const notificationsApi = {
  /**
   * Get notifications for the current user
   */
  getNotifications: async (
    unreadOnly = false,
    options?: { includeArchived?: boolean; limit?: number; offset?: number }
  ): Promise<NotificationListResponse> => {
    const params = new URLSearchParams();
    params.set('unread_only', String(unreadOnly));
    if (options?.includeArchived !== undefined) {
      params.set('include_archived', String(options.includeArchived));
    }
    if (options?.limit) {
      params.set('limit', String(options.limit));
    }
    if (options?.offset) {
      params.set('offset', String(options.offset));
    }
    const response = await apiClient.get<unknown>(
      `/notifications?${params.toString()}`
    );
    const data = response.data as Record<string, unknown>;
    return {
      notifications: ((data.notifications as unknown[]) ?? []).map(transformNotification),
      total: data.total as number,
      unreadCount: (data.unreadCount ?? data.unread_count) as number,
    };
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get<{ count: number }>('/notifications/unread-count');
    return response.data;
  },

  /**
   * Get a specific notification
   */
  getNotification: async (notificationId: string): Promise<Notification> => {
    const response = await apiClient.get<unknown>(`/notifications/${notificationId}`);
    return transformNotification(response.data);
  },

  /**
   * Mark notifications as read
   */
  markAsRead: async (notificationIds: string[]): Promise<void> => {
    await apiClient.post('/notifications/mark-read', { notification_ids: notificationIds });
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (before?: string): Promise<void> => {
    await apiClient.post('/notifications/mark-all-read', { before });
  },

  /**
   * Mark a notification as clicked (navigated to)
   */
  clickNotification: async (notificationId: string): Promise<Notification> => {
    const response = await apiClient.post<unknown>(`/notifications/${notificationId}/click`);
    return transformNotification(response.data);
  },

  /**
   * Archive a notification
   */
  archiveNotification: async (notificationId: string): Promise<Notification> => {
    const response = await apiClient.post<unknown>(
      `/notifications/${notificationId}/archive`
    );
    return transformNotification(response.data);
  },

  /**
   * Archive all notifications
   */
  archiveAll: async (): Promise<void> => {
    await apiClient.post('/notifications/archive-all');
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId: string): Promise<void> => {
    await apiClient.delete(`/notifications/${notificationId}`);
  },
};

// =============================================================================
// Transform helpers (snake_case to camelCase)
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
// =============================================================================

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
function transformAttachment(data: any): Attachment {
  const attachment: Attachment = {
    id: data.id,
    filename: data.filename,
    contentType: data.contentType ?? data.content_type,
    attachmentType: data.attachmentType ?? data.attachment_type,
    sizeBytes: data.sizeBytes ?? data.size_bytes,
    status: data.status,
    uploadedById: data.uploadedById ?? data.uploaded_by_id,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
  if (data.url) attachment.url = data.url;
  if (data.thumbnailUrl ?? data.thumbnail_url) attachment.thumbnailUrl = data.thumbnailUrl ?? data.thumbnail_url;
  return attachment;
}

function transformAuthor(data: any): CommentAuthor | undefined {
  if (!data) return undefined;
  return {
    id: data.id,
    displayName: data.displayName ?? data.display_name,
    avatarUrl: data.avatarUrl ?? data.avatar_url,
  };
}

function transformComment(data: any): Comment {
  const comment: Comment = {
    id: data.id,
    taskId: data.taskId ?? data.task_id,
    authorId: data.authorId ?? data.author_id,
    content: data.content,
    commentType: data.commentType ?? data.comment_type,
    depth: data.depth,
    replyCount: data.replyCount ?? data.reply_count ?? 0,
    attachments: (data.attachments ?? []).map(transformAttachment),
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
  const author = transformAuthor(data.author);
  if (author) comment.author = author;
  const parentId = data.parentId ?? data.parent_id;
  if (parentId) comment.parentId = parentId;
  if (data.mentions) comment.mentions = data.mentions;
  if (data.reactions) comment.reactions = data.reactions;
  const editedAt = data.editedAt ?? data.edited_at;
  if (editedAt) comment.editedAt = editedAt;
  return comment;
}

function transformAttachmentUploadResponse(data: any): AttachmentUploadResponse {
  return {
    attachmentId: data.attachmentId ?? data.attachment_id,
    uploadUrl: data.uploadUrl ?? data.upload_url,
    expiresAt: data.expiresAt ?? data.expires_at,
  };
}

function transformNotification(data: any): Notification {
  const notification: Notification = {
    id: data.id,
    userId: data.userId ?? data.user_id,
    notificationType: data.notificationType ?? data.notification_type,
    title: data.title,
    message: data.message,
    priority: data.priority,
    isRead: data.isRead ?? data.is_read,
    isClicked: data.isClicked ?? data.is_clicked,
    isArchived: data.isArchived ?? data.is_archived,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
  const actionUrl = data.actionUrl ?? data.action_url;
  if (actionUrl) notification.actionUrl = actionUrl;
  const sourceTaskId = data.sourceTaskId ?? data.source_task_id;
  if (sourceTaskId) notification.sourceTaskId = sourceTaskId;
  const sourceCommentId = data.sourceCommentId ?? data.source_comment_id;
  if (sourceCommentId) notification.sourceCommentId = sourceCommentId;
  const sourceProjectId = data.sourceProjectId ?? data.source_project_id;
  if (sourceProjectId) notification.sourceProjectId = sourceProjectId;
  const sourceMeetingId = data.sourceMeetingId ?? data.source_meeting_id;
  if (sourceMeetingId) notification.sourceMeetingId = sourceMeetingId;
  const actorId = data.actorId ?? data.actor_id;
  if (actorId) notification.actorId = actorId;
  const actor = transformAuthor(data.actor);
  if (actor) notification.actor = actor;
  const readAt = data.readAt ?? data.read_at;
  if (readAt) notification.readAt = readAt;
  const clickedAt = data.clickedAt ?? data.clicked_at;
  if (clickedAt) notification.clickedAt = clickedAt;
  return notification;
}
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

export default { commentsApi, notificationsApi };
